<template>
  <q-btn
    no-caps
    color="grey-5"
    outline
    dense
    style="font-size: small"
    :style="btnStyle"
    class="q-py-sm q-px-md"
    :class="!$q.dark.isActive ? 'bg-white' : ''"
  >
    <q-icon
      v-if="!dense"
      name="event"
      class="button-content"
      size="xs"
      :class="!$q.dark.isActive ? 'text-black' : 'text-white'"
    />
    <div
      v-if="value"
      class="q-ml-sm button-content"
      :class="!$q.dark.isActive ? 'text-black' : 'text-white'"
    >
      <span v-if="typeof value === 'object'">
        {{ formatButtons(value.from) }}
        <q-icon name="arrow_forward" class="text-grey-6" size="1em" />
        {{ formatButtons(value.to) }}
      </span>
      <span v-else>
        {{ formatButtons(value) }}
      </span>
    </div>
    <div
      v-else
      class="q-ml-sm button-content"
      :class="!$q.dark.isActive ? 'text-black' : 'text-white'"
    >
      {{ btnLabel }}
    </div>
    <q-menu transition-duration="100" @before-hide="beforeHide">
      <div class="flex">
        <div
          style="width: 170px; max-height: 450px"
          :style="`border-right: 1px solid ${$q.dark.isActive ? 'rgba(255, 255, 255, 0.28)' : 'rgba(0, 0, 0, 0.12)'}`"
          class="column"
        >
          <div class="col">
            <div class="title">Quick Ranges</div>
            <div
              class="option"
              :class="{ 'selected-option': selectedPreset === 'today' }"
              @click="updateDateRange(presetRanges['today'])"
            >
              Today
            </div>
            <div
              class="option"
              :class="{ 'selected-option': selectedPreset === 'last 7 days' }"
              @click="updateDateRange(presetRanges['last 7 days'])"
            >
              Last 7 days
            </div>
            <div
              class="option"
              :class="{ 'selected-option': selectedPreset === 'last week mon-sun' }"
              @click="updateDateRange(presetRanges['last week mon-sun'])"
            >
              Last Week Mon-Sun
            </div>
            <div
              class="option"
              :class="{ 'selected-option': selectedPreset === 'last 14 days' }"
              @click="updateDateRange(presetRanges['last 14 days'])"
            >
              Last 14 days
            </div>
            <div
              class="option"
              :class="{ 'selected-option': selectedPreset === 'last 30 days' }"
              @click="updateDateRange(presetRanges['last 30 days'])"
            >
              Last 30 days
            </div>
            <div
              class="option"
              :class="{ 'selected-option': selectedPreset === 'last 2 months' }"
              @click="updateDateRange(presetRanges['last 2 months'])"
            >
              Last 2 months
            </div>
            <div
              class="option"
              :class="{ 'selected-option': selectedPreset === 'last 3 months' }"
              @click="updateDateRange(presetRanges['last 3 months'])"
            >
              Last 3 months
            </div>
            <div
              class="option"
              :class="{ 'selected-option': selectedPreset === 'last 6 months' }"
              @click="updateDateRange(presetRanges['last 6 months'])"
            >
              Last 6 months
            </div>
            <div
              class="option"
              :class="{ 'selected-option': selectedPreset === 'last year' }"
              @click="updateDateRange(presetRanges['last year'])"
            >
              Last year
            </div>
            <div
              class="option"
              :class="{ 'selected-option': selectedPreset === 'all history' }"
              @click="updateDateRange(presetRanges['all history'])"
            >
              All history
            </div>
          </div>
          <div class="col-shrink">
            <q-separator />
            <div class="custom-option flex">
              <q-select
                v-model="selectedMonth"
                dense
                fill
                outlined
                :options="pastMonths"
                :option-label="(month) => `${month.month} ${month.year}`"
                :hide-bottom-space="true"
                label="Full Month"
                style="width: 150px"
                @update:model-value="setMonth()"
              >
                <template #selected>
                  <div style="font-size: 10px">
                    {{ selectedMonth?.month }} {{ selectedMonth?.year }}
                  </div>
                </template>
              </q-select>
            </div>
          </div>
        </div>
        <div>
          <div class="flex items-center q-pa-sm" :class="{ 'bg-grey-9': $q.dark.isActive }">
            <div class="col">
              <q-input
                v-model="fromDate"
                dense
                outlined
                hide-bottom-space
                mask="##/##/####"
                :rules="[dateValidator]"
                class="col"
                style="width: 115px"
                no-error-icon
                @blur="updateFromDate"
                @keyup.enter="dateValidator(fromDate) === true && updateFromDate()"
              />
            </div>
            <q-icon name="arrow_forward" class="q-mx-md text-grey-6" />
            <div class="col">
              <q-input
                v-model="toDate"
                dense
                outlined
                hide-bottom-space
                mask="##/##/####"
                :rules="[dateValidator]"
                class="col"
                style="width: 115px"
                no-error-icon
                @blur="updateToDate"
                @keyup.enter="dateValidator(toDate) === true && updateToDate()"
              />
            </div>
          </div>
          <q-separator />
          <q-date
            v-model="value"
            flat
            minimal
            range
            :options="useDateConstraints ? dateConstraints : undefined"
            mask="YYYY-MM-DD"
            @update:model-value="updateModelValue"
          />
        </div>
      </div>
    </q-menu>
  </q-btn>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import {
  format,
  addDays,
  parseISO,
  differenceInDays,
  eachMonthOfInterval,
  addMonths,
  lastDayOfMonth,
  parse,
  isValid
} from 'date-fns'

type PresetRange =
  | 'today'
  | 'last 7 days'
  | 'last week mon-sun'
  | 'last 14 days'
  | 'last 30 days'
  | 'last 2 months'
  | 'last 3 months'
  | 'last 6 months'
  | 'last year'
  | 'all history'

export default {
  name: 'DateRangePicker',
  components: {},
  props: {
    dense: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      required: true
    },
    dateRange: {
      type: Object as PropType<{ from: Date; to: Date }>,
      default() {
        return {
          from: null,
          to: null
        }
      }
    },
    btnStyle: {
      type: String,
      default: ''
    },
    btnLabel: {
      type: String,
      default: 'Date Range'
    },
    useDateConstraints: {
      type: Boolean,
      default: true
    },
    footer: {
      type: Boolean,
      default: true
    }
  },
  emits: ['update'],
  data() {
    return {
      value: { from: format(new Date(), 'yyyy-MM-dd'), to: format(new Date(), 'yyyy-MM-dd') } as
        | { from: string; to: string }
        | string
        | null,
      fromMenuOpen: false,
      toMenuOpen: false,
      daysUntil: null as number | null,
      selectedMonth: null as { month: string; year: number } | null,
      minDate: new Date(2023, 5, 17),
      maxDate: new Date(),
      monthOptions: [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec'
      ] as string[],
      fromDate: '',
      toDate: ''
    }
  },
  computed: {
    allHistoryDays() {
      return differenceInDays(new Date(), new Date('2023/06/17'))
    },
    pastMonths() {
      const currentDate = new Date()
      const startDate = addMonths(currentDate, -12)
      const months = eachMonthOfInterval({ start: startDate, end: currentDate })

      return months
        .map((month, index) => {
          return {
            month: this.monthOptions[month.getMonth()],
            year: month.getFullYear(),
            index
          }
        })
        .filter((month) => month.index !== 12)
        .sort((a, b) => b.index - a.index)
    },
    presetRanges(): Record<PresetRange, { from: Date; to: Date }> {
      return {
        today: this.rangeFromXDaysAgo(1),
        'last 7 days': this.rangeFromXDaysAgo(7),
        'last week mon-sun': this.rangeFromLastWeek(),
        'last 14 days': this.rangeFromXDaysAgo(14),
        'last 30 days': this.rangeFromXDaysAgo(30),
        'last 2 months': this.rangeFromXDaysAgo(60),
        'last 3 months': this.rangeFromXDaysAgo(90),
        'last 6 months': this.rangeFromXDaysAgo(180),
        'last year': this.rangeFromXDaysAgo(365),
        'all history': this.rangeFromXDaysAgo(differenceInDays(new Date(), new Date('2023/06/17')))
      }
    },
    selectedPreset(): PresetRange | null {
      if (!(this.dateRange.to && this.dateRange.from)) {
        return null
      }

      for (const key in this.presetRanges) {
        const preset = this.presetRanges[key]
        const fromMatch =
          preset.from.getDate() === this.dateRange.from.getDate() &&
          preset.from.getMonth() === this.dateRange.from.getMonth() &&
          preset.from.getYear() === this.dateRange.from.getYear()
        const toMatch =
          preset.to.getDate() === this.dateRange.to.getDate() &&
          preset.to.getMonth() === this.dateRange.to.getMonth() &&
          preset.to.getYear() === this.dateRange.to.getYear()

        if (fromMatch && toMatch) {
          return key
        }
      }

      return null
    }
  },
  watch: {
    dateRange() {
      this.dateRangeChange()
    },
    value: {
      handler(newValue) {
        if (newValue && typeof newValue === 'object') {
          this.fromDate = format(parseISO(newValue.from), 'MM-dd-yyyy')
          this.toDate = format(parseISO(newValue.to), 'MM-dd-yyyy')
        } else if (newValue && typeof newValue === 'string') {
          this.fromDate = format(parseISO(newValue), 'MM-dd-yyyy')
          this.toDate = format(parseISO(newValue), 'MM-dd-yyyy')
        }
      },
      immediate: true
    }
  },
  mounted() {
    this.dateRangeChange()
  },
  methods: {
    setMonth() {
      // change from and to to the first and last day of the selected month
      const selectedMonth = this.selectedMonth
      if (!selectedMonth) {
        return
      }
      const from = new Date(selectedMonth.year, this.monthOptions.indexOf(selectedMonth.month), 1)
      const to = lastDayOfMonth(from)
      this.updateDateRange({ from, to })
    },
    formatButtons(date: string) {
      return format(parseISO(date), 'MMM d, yyyy')
    },
    dateConstraints(date: string) {
      return date >= '2023/06/17' && date <= format(new Date(), 'yyyy/MM/dd')
    },
    updateDateRange(dateRange: { from: Date; to: Date }) {
      // inverse minDate and maxDate if the from date is after the to date
      if (dateRange.from > dateRange.to) {
        ;[dateRange.from, dateRange.to] = [dateRange.to, dateRange.from]
      }

      // Block dates before minDate and after maxDate
      if (dateRange.from < this.minDate) {
        dateRange.from = this.minDate
      }
      if (dateRange.to > this.maxDate) {
        dateRange.to = this.maxDate
      }
      if (dateRange.to.toLocaleDateString() === dateRange.from.toLocaleDateString()) {
        dateRange.from.setDate(dateRange.from.getDate() - 1)
      }

      this.$emit('update', dateRange)
      this.$nextTick(() => {
        this.$q.notify({
          message: 'Date range updated',
          color: 'positive',
          position: 'bottom',
          timeout: 750,
          icon: 'check',
          iconColor: 'white'
        })
      })
    },
    rangeFromXDaysAgo(numDays: number): { from: Date; to: Date } {
      const to = new Date()
      const from = addDays(to, -numDays)

      return { from, to }
    },
    rangeFromLastWeek(): { from: Date; to: Date } {
      const today = new Date()
      const daysSince = {
        priorMonday: (today.getDay() + 6) % 14,
        priorSunday: today.getDay() % 7
      }

      const from = new Date()
      from.setDate(today.getDate() - daysSince.priorMonday)
      const to = new Date()
      to.setDate(today.getDate() - daysSince.priorSunday)

      return { from, to }
    },
    dateRangeChange() {
      if (this.dateRange.to && this.dateRange.from) {
        if (this.dateRange.to.toDateString() === this.dateRange.from.toDateString()) {
          this.value = format(this.dateRange.to, 'yyyy-MM-dd')
        } else {
          this.value = {
            to: format(this.dateRange.to, 'yyyy-MM-dd'),
            from: format(this.dateRange.from, 'yyyy-MM-dd')
          }
        }
      }
    },
    updateModelValue() {
      let minDate = null as Date | null
      let maxDate = null as Date | null

      if (this.value && typeof this.value === 'object') {
        minDate = this.value.from ? parseISO(this.value.from) : null
        maxDate = this.value.to ? parseISO(this.value.to) : null
      } else if (this.value) {
        minDate = parseISO(this.value)
        maxDate = parseISO(this.value)
      }
      if (minDate && maxDate) {
        this.updateDateRange({ from: minDate, to: maxDate })
      }
    },
    dateValidator(date: string) {
      const parsedDate = parse(date, 'MM/dd/yyyy', new Date())
      if (parsedDate < this.minDate) {
        return 'Date is before 06/17/2023'
      }
      if (parsedDate > this.maxDate) {
        return 'Date is after today'
      }
      return isValid(parsedDate) || 'Invalid date format (MM/DD/YYYY)'
    },
    beforeHide() {
      this.updateFromDate()
      this.updateToDate()
    },
    updateFromDate() {
      const newValue = parse(this.fromDate, 'MM/dd/yyyy', new Date())
      if (!isValid(newValue)) {
        return
      }
      const newValueStr = format(newValue, 'yyyy-MM-dd')
      if (
        this.dateValidator(this.fromDate) === true &&
        ((this.value && typeof this.value === 'object' && newValueStr !== this.value?.from) ||
          (this.value && typeof this.value === 'string' && newValueStr !== this.value))
      ) {
        const parsedDate = parse(this.fromDate, 'MM/dd/yyyy', new Date())
        if (this.value && typeof this.value === 'object') {
          this.value = { ...this.value, from: format(parsedDate, 'yyyy-MM-dd') }
          this.updateModelValue()
        }
      }
    },
    updateToDate() {
      const newValue = parse(this.toDate, 'MM/dd/yyyy', new Date())
      if (!isValid(newValue)) {
        return
      }
      const newValueStr = format(newValue, 'yyyy-MM-dd')
      if (
        this.dateValidator(this.toDate) === true &&
        ((this.value && typeof this.value === 'object' && newValueStr !== this.value?.to) ||
          (this.value && typeof this.value === 'string' && newValueStr !== this.value))
      ) {
        const parsedDate = parse(this.toDate, 'MM/dd/yyyy', new Date())
        if (this.value && typeof this.value === 'object') {
          this.value = { ...this.value, to: format(parsedDate, 'yyyy-MM-dd') }
          this.updateModelValue()
        }
      }
    }
  }
}
</script>

<style scoped lang="scss">
.custom-option {
  margin: 0px 0px;
  padding: 4px 10px;
}
.option {
  margin: 2px 10px;
  padding: 3px 10px;
  cursor: pointer;
  border-radius: 5px;

  &:first-child {
    margin-top: 10px;
  }

  &:last-child {
    margin-bottom: 10px;
  }

  &:hover {
    background-color: $primary;
    color: white;
    font-weight: 500;
  }
  &.selected-option {
    background-color: $primary;
    color: white;
    font-weight: 500;
  }
}
.title {
  margin: 4px 10px 0 10px;
  padding: 10px 10px 5px 10px;
  font-weight: 500;
}
.button-content {
  font-weight: 400;
}
.formatButtons {
  min-height: 150px;
}
</style>
